import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Col,
  Row,
  NMForm,
  Button,
  usePub,
  OutlinedButton,
} from "@neumetric/blueprint-lib/components";
import { useDispatch, useSelector } from "react-redux";
import {
  mitigatorActions,
  getCurrentThreat,
  getThreatTypesByStatus,
  getSourcesByStatus,
} from "../../../store";

const getThreatFields = (isViewMode, isEditMode) => {
  const fields = [];
  if (isViewMode || isEditMode) {
    fields.push({
      label: "Threat ID",
      labelAlign: "top",
      attribute: "threat_id",
      placeholder: "Threat ID",
      typeRepresentation: "TEXT",
      classes: {
        root: "w-100",
        label: "w-91-px",
        input: "pl-12",
      },
    });
  }
  fields.push(
    {
      label: "Source",
      labelAlign: "top",
      attribute: "source",
      placeholder: "Source",
      typeRepresentation: isViewMode ? "TEXT" : "DROPDOWN",
      classes: {
        root: "w-100",
        label: "w-91-px",
        input: isViewMode ? "bg-contrast pl-12" : "bg-contrast",
      },
    },
    {
      label: "Threat Type",
      labelAlign: "top",
      attribute: "threat_type",
      placeholder: "Threat Type",
      typeRepresentation: isViewMode ? "TEXT" : "DROPDOWN",
      classes: {
        root: "w-100",
        input: isViewMode ? "bg-contrast pl-12" : "bg-contrast",
        label: "text-right w-91-px",
      },
    },
    {
      label: "Threat",
      labelAlign: "top",
      attribute: "threat",
      typeRepresentation: "TEXT",
      classes: { root: "w-100", label: "text-right w-91-px", input: "pl-12" },
    },
    {
      label: "Description",
      labelAlign: "top",
      attribute: "description",
      typeRepresentation: isViewMode ? "TEXT" : "TEXTAREA",
      classes: { root: "w-100", label: "text-right w-91-px", input: "pl-12" },
    },
  );
  return fields;
};

export const CreateNewThreat = () => {
  const dispatch = useDispatch();
  const publish = usePub();
  const [submitting, setSubmitting] = React.useState(false);
  const _ref = React.useRef(null);
  const viewRef = React.useRef(null);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const currentTab = params.get("tab");
  const threatId = params.get("id");
  const currentThreat = useSelector(getCurrentThreat);
  const sourceList = useSelector(getSourcesByStatus.bind(null, "active")) || [];
  const threatTypeList =
    useSelector(getThreatTypesByStatus.bind(null, "active")) || [];
  const [isEditMode, setIsEditMode] = useState(false);
  useEffect(() => {
    dispatch(mitigatorActions.fetchSources());
    dispatch(mitigatorActions.fetchThreatTypes());
  }, []);
  useEffect(() => {
    if (currentThreat && viewRef.current) {
      viewRef.current.updateFormData({
        threat_id: currentThreat.threat_id,
        source: currentThreat.source,
        threat_type: currentThreat.threat_type,
        threat: currentThreat.threat,
        description: currentThreat.description,
      });
    }
  }, [currentThreat]);

  useEffect(() => {
    if (!threatId) return;
    dispatch(mitigatorActions.fetchThreat({ threat_id: threatId }));
  }, [threatId]);

  useEffect(() => {
    if (
      isEditMode &&
      _ref.current &&
      sourceList.length > 0 &&
      threatTypeList.length > 0 &&
      currentThreat
    ) {
      _ref.current.updateFormData({
        source: String(currentThreat.source_id),
        threat_type: String(currentThreat.threat_type_id),
      });
    }
  }, [isEditMode, sourceList, threatTypeList, currentThreat]);

  const getOptions = (attribute) => {
    if (attribute === "source") {
      return sourceList.map((s) => ({
        id: String(s.id),
        label: s.name,
      }));
    }
    if (attribute === "threat_type") {
      return threatTypeList.map((t) => ({
        id: String(t.id),
        label: t.threat_type,
      }));
    }
    return [];
  };

  const onComplete = () => {
    setSubmitting(false);
    setIsEditMode(false); // return to view mode
    publish("MITIGATOR_HIDE_NEW_CONTENT");
  };
  const onFailure = () => {
    setSubmitting(false);
  };
  const handleSave = () => {
    if (!_ref.current.validate()) {
      return;
    }
    const values = _ref.current.getValues();
    const body = {
      source_id: values.source,
      threat_type_id: values.threat_type,
      threat: values.threat,
      description: values.description,
      status: "draft",
    };
    // const action = mitigatorActions.createThreat(body);
    const action = isEditMode
      ? mitigatorActions.updateThreat({
        id: threatId,
        status: currentThreat?.status,
        ...body,
      })
      : mitigatorActions.createThreat(body);
    action.onComplete = onComplete;
    action.onFailure = onFailure;
    setSubmitting(true);
    dispatch(action);
  };
  const handleCancel = () => {
    if (submitting) return;
    _ref.current?.reset?.();
    publish("MITIGATOR_HIDE_NEW_CONTENT"); //hides create panel
  };
  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        padding: "0 12px",
        boxSizing: "border-box",
      }}
    >
      {threatId && currentThreat && !isEditMode ? (
        <Col className="w-100 h-100 overflow-hide mt-36 pb-16">
          <Row justify="between" align="" className="w-100 pl-16 pr-16">
            <span className="ff-exo2 fw-500 f22 line-28 mb2 c238787">
              Threat
            </span>
            <div className="pr-10">
              <Button
                label="Close"
                icon="CANCEL"
                onClick={() => publish("MITIGATOR_HIDE_NEW_CONTENT")}
              />
            </div>
          </Row>
          {/* VIEW MODE FORM */}
          <NMForm
            className="w-100 pl-30 pr-26"
            ref={viewRef}
            readOnly={true}
            fields={getThreatFields(true, false)}
            formData={{
              threat_id: currentThreat.threat_id,
              source: currentThreat.source,
              threat_type: currentThreat.threat_type,
              threat: currentThreat.threat,
              description: currentThreat.description,
            }}
          />
          <Row
            className="pr-36 mt-16 w-100"
            justify="end"
            align="center"
            style={{ gap: "12px" }}
          >
            {currentTab === "draft" && (
              <>
                <Button
                  label="Edit"
                  icon="EDIT"
                  onClick={() => setIsEditMode(true)}
                />
                <Button
                  label="Activate"
                  icon="CHECK_CONFIRM"
                  onClick={() => {
                    dispatch(
                      mitigatorActions.updateThreat({
                        id: threatId,
                        threat_id: currentThreat.threat_id,
                        source_id: currentThreat.source_id,
                        threat_type_id: currentThreat.threat_type_id,
                        threat: currentThreat.threat,
                        description: currentThreat.description,
                        status: "active",
                      }),
                    );

                    publish("MITIGATOR_HIDE_NEW_CONTENT");
                  }}
                />
              </>
            )}
            {currentTab === "active" && (
              <>
                <Button
                  label="Archive"
                  icon="OPEN_FOLDER"
                  variant="CAUTION"
                  onClick={() => {
                    dispatch(
                      mitigatorActions.updateThreat({
                        id: threatId,
                        threat_id: currentThreat.threat_id,
                        source_id: currentThreat.source_id,
                        threat_type_id: currentThreat.threat_type_id,
                        threat: currentThreat.threat,
                        description: currentThreat.description,
                        status: "archived",
                      }),
                    );

                    publish("MITIGATOR_HIDE_NEW_CONTENT");
                  }}
                />
              </>
            )}
            {currentTab === "archive" && (
              <>
                <Button
                  label="Unarchive"
                  icon="OPEN_FOLDER"
                  onClick={() => {
                    dispatch(
                      mitigatorActions.updateThreat({
                        id: threatId,
                        threat_id: currentThreat.threat_id,
                        source_id: currentThreat.source_id,
                        threat_type_id: currentThreat.threat_type_id,
                        threat: currentThreat.threat,
                        description: currentThreat.description,
                        status: "draft",
                      }),
                    );

                    publish("MITIGATOR_HIDE_NEW_CONTENT");
                  }}
                />
              </>
            )}
          </Row>
        </Col>
      ) : (
        <>
          <Col className="w-100 h-100 overflow-hide mt-36 pb-16">
            <Row justify="between" align="center" className="w-100 pl-16 pr-16">
              <span className="ff-exo2 fw-500 f22 line-28  mb2 c238787 ">
                Threat
              </span>
            </Row>
            <NMForm
              className="w-100 pl-30 pr-26"
              ref={_ref}
              // formData={{ id: "Threat ID" }}
              formData={
                isEditMode
                  ? {
                    threat_id: currentThreat?.threat_id ?? "",
                    source: currentThreat?.source ?? "",
                    threat_type: currentThreat?.threat_type ?? "",
                    threat: currentThreat?.threat ?? "",
                    description: currentThreat?.description ?? "",
                  }
                  : {
                    threat_id: "",
                    source: "",
                    threat_type: "",
                    threat: "",
                    description: "",
                  }
              }
              id="new-threat-form"
              fields={
                isEditMode
                  ? getThreatFields(false, true)
                  : getThreatFields(false, false)
              }
              getOptions={getOptions}
            />
            <Row
              className="pr-26 mt-16 w-100"
              justify="end"
              align="center"
              style={{ gap: "12px" }}
            >
              {" "}
              <OutlinedButton
                label="Cancel"
                icon="CANCEL"
                onClick={handleCancel}
                disabled={submitting}
              />
              <Button
                label="Save"
                icon="SAVE"
                onClick={handleSave}
                loading={submitting}
              />
            </Row>
          </Col>
        </>
      )}
    </div>
  );
};
